/**
 * A request's approval levels as every screen reads them, web and phone.
 *
 * One definition: a tracker that says APPROVED on one screen and PENDING on
 * another is worse than none, and the rule was already written twice once.
 * The markup lives with each app; the reading of a level, and the words for
 * it in the reader's language, live here.
 */
import type { TranslateVars } from '../i18n/index';

export interface ApprovalTrailStep {
  readonly instanceStepId: string;
  readonly stepSequence: number;
  readonly stepName: string | null;
  readonly stepStatus: string;
  readonly assignedRoleCode: string | null;
  readonly actionedAt: string | null;
  readonly actionedByName: string | null;
  readonly actionedByLoginId: string | null;
  readonly remarks: string | null;
  readonly approvers: readonly {
    readonly userId?: string;
    readonly name: string;
    readonly loginId: string | null;
  }[];
  /** The level belongs to a party (the job's vendor) the record has not named yet. */
  readonly awaitsParty?: boolean;
}

export type ApprovalLevelState = 'done' | 'rejected' | 'current' | 'todo';

export function approvalLevelState(stepStatus: string): ApprovalLevelState {
  if (stepStatus === 'APPROVED') return 'done';
  if (stepStatus === 'REJECTED' || stepStatus === 'RETURNED') return 'rejected';
  if (stepStatus === 'PENDING') return 'current';
  return 'todo';
}

/** "Name (login id)", or whichever of the two is known. */
export function personLabel(
  name: string | null,
  loginId: string | null,
): string | null {
  if (!name) return loginId;
  return loginId ? `${name} (${loginId})` : name;
}

type Translate = (key: string, vars?: TranslateVars) => string;

/** Who holds a level, in the reader's language. */
export function approverNames(step: ApprovalTrailStep, t: Translate): string {
  const named = step.approvers
    .map((a) => personLabel(a.name, a.loginId))
    .filter((x): x is string => Boolean(x));
  if (named.length > 0) return named.join(', ');
  if (step.awaitsParty && step.assignedRoleCode) {
    return t('approvals.detail.partyNotNamed', { role: step.assignedRoleCode });
  }
  return step.assignedRoleCode ?? '\u2014';
}

/** A level's marker, e.g. "Level 2". */
export function approvalLevelName(step: ApprovalTrailStep, t: Translate): string {
  return t('approvals.detail.level', { n: step.stepSequence });
}

/** Where a level stands - approved by whom and when, rejected, awaited or not started. */
export function approvalLevelLine(
  step: ApprovalTrailStep,
  t: Translate,
  at: (iso: string) => string,
): string {
  const state = approvalLevelState(step.stepStatus);
  const actor = personLabel(step.actionedByName, step.actionedByLoginId) ?? approverNames(step, t);
  if (state === 'done') {
    return t('approvals.detail.approvedBy', {
      user: actor,
      when: step.actionedAt ? at(step.actionedAt) : '\u2014',
    });
  }
  if (state === 'rejected') return t('approvals.detail.rejectedBy', { user: actor });
  if (state === 'current') return t('approvals.detail.awaiting', { user: approverNames(step, t) });
  return t('approvals.detail.notStarted', { user: approverNames(step, t) });
}
